import { StatusCode } from "api/protocols";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetProductTypes } from "api/rpc/2024-04/facilityAdmin/product/type";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { IReward } from "redux/reducers/models/loyalty";
import { IProduct, TProductType } from "redux/reducers/models/product";
import { Select } from "components/select/index";
import "../loyalty.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faXmark } from "@fortawesome/pro-regular-svg-icons";

interface IProps {
    open: boolean;
    onCancel: () => void;
    onOk: (offer: Partial<IReward & {product_type_ids?: Array<number>; product_ids?: Array<number>}>) => void;
    reward?: IReward & {products?: Array<IProduct>; product_types?: Array<TProductType>};
}

interface IState {
    id?: number;
    name: string;
    reward_type: "discount_percent" | "discount_amount" | "free_product";
    application: "all_products" | "product_categories" | "product" | "order";
    discount_value?: string;
    points_required?: string;
}


export default function LoyaltyProgramRewardsModal(props: IProps) {
    const {open, onCancel, onOk, reward} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const { getCurrencySymbol } = useCurrencyFacility();
    const [types, setTypes] = useState<Array<TProductType>>([]);
    const [selectedTypes, setSelectedTypes] = useState<Array<TProductType & {name?: string}>>([]);
    const [typeSearch, setTypeSearch] = useState<string>("");
    const [products, setProducts] = useState<Array<IProduct & {name?: string}>>([]);
    const [selectedProducts, setSelectedProducts] = useState<Array<IProduct & {name?: string}>>([]);
    const [productSearch, setProductSearch] = useState<string>("");
    const {facilityStore} = useAppSelector(store => store);
    const [state, setState] = useState<IState>({
        id: null,
        name: "",
        reward_type: "discount_percent",
        application: "product",
        discount_value: "",
        points_required: "",
    });
    const { Option } = Select;

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getProductTypes(source.token);
        return () => source.cancel();
    }, []);
    
    useEffect(() => {
        const source = axios.CancelToken.source();
        let timeoutId: NodeJS.Timeout = null;
        timeoutId = global.setTimeout(() => {
            void getProducts(source.token);
          }, 500);
    
        return () => {
          clearTimeout(timeoutId);
          source.cancel();
        };
      }, [productSearch]);

    useEffect(() => {
        if (!open) {
            ReactDOM.unstable_batchedUpdates(() => {
                setState({
                    id: null,
                    name: "",
                    reward_type: "discount_percent",
                    application: "product",
                    discount_value: "",
                    points_required: "",
                });
                setSelectedTypes([]);
                setSelectedProducts([]);
                setProductSearch("");
                setTypeSearch("");
            });
        } else {
            if (reward) {
                setState({
                    ...reward,
                    discount_value: reward?.discount_value?.toString(),
                    points_required: reward?.points_required?.toString(),
                });
                if (reward?.application === "product" && reward?.products?.length > 0) {
                    setSelectedProducts([...reward.products]?.map(product => ({...product, name: product?.preferred_title ? product?.preferred_title : product?.title})));
                } else if (reward?.application === "product_categories" && reward?.product_types?.length > 0) {
                    setSelectedTypes([...reward.product_types]?.map(type => ({...type, name: type?.title})));
                }
            }
        }
    }, [open]);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const {id, value } = e.target;

        setState(prev => ({...prev, [id]: value}));
    }
    async function getProductTypes(cancelToken?: CancelToken) {
        const res = await GetProductTypes(null, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting product types")));
            return;
        }
        setTypes(res?.data);
    }

    async function getProducts(cancelToken?: CancelToken) {
        //loader
        if (products !== undefined) {
            setProducts(undefined);
        }
        const res = await GetProduct({limit: 50, search: productSearch}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setProducts([]);
            dispatch(showError(t("Error getting products")));
            return;
        }
        setProducts(res?.data);
    }

    function handleOk(reward: Partial<IReward & {
        product_type_ids?: number[];
        product_ids?: number[];
    }>) {
        if (!state?.name) {
            dispatch(showError(t("Reward name required")));
            return;
        } else if (state?.reward_type === "discount_percent" && Math.abs(Number(state?.discount_value)) > 100) {
            dispatch(showError(t("Discount percent cannot be over 100")));
            return;
        }

        void onOk(reward);
    }

    return (
        <Portal isMounted={open}>
        <Sheet
            open={open}
            size="small"
            title={t("Reward")}
            supportText={t("Choose what customers can redeem their points for")}
            closable
            onCancel={onCancel}
            onOk={() => handleOk({
                ...state,
                discount_value: Math.abs(Number(state?.discount_value)),
                points_required: Math.round(Math.abs(Number(state?.points_required))),
                product_ids: state?.application === "product" ? selectedProducts?.map(product => product?.id) : undefined,
                product_type_ids: state?.application === "product_categories" ? selectedTypes?.map(type => type?.id) : undefined,
            })}
            okText={t("Save")}
            // overflow
            >
                <FormLayout>
                <FormLayout.Group>
                    <p className="loyalty-checkbox-title">{t("Select the Reward Type")}:</p>
                </FormLayout.Group>
                <FormLayout.Group>
                   <div className={classNames("loyalty-checkbox-container", {"loyalty-checkbox-container-active": state?.reward_type === "discount_percent"})} onClick={() => setState(prev => ({...prev, reward_type: "discount_percent"}))}>
                       <Checkbox size="small" circle labelHidden checked={state?.reward_type === "discount_percent"} onChange={() => {}}/>
                       <p>{t("Discount (%)")}</p>
                   </div>
                   <div className={classNames("loyalty-checkbox-container", {"loyalty-checkbox-container-active": state?.reward_type === "discount_amount"})} onClick={() => setState(prev => ({...prev, reward_type: "discount_amount"}))}>
                       <Checkbox size="small" circle labelHidden checked={state?.reward_type === "discount_amount"} onChange={() => {}}/>
                       <p>{t("Discount")} ({getCurrencySymbol(facilityStore?.facility?.default_currency_code)})</p>
                   </div>
                   <div className={classNames("loyalty-checkbox-container", {"loyalty-checkbox-container-active": state?.reward_type === "free_product"})} onClick={() => setState(prev => ({...prev, reward_type: "free_product", discount_value: "100", application: prev?.application === "order" ? "product" : prev?.application}))}>
                       <Checkbox size="small" circle labelHidden checked={state?.reward_type === "free_product"} onChange={() => {}}/>
                       <p>{t("Free Product")}</p>
                   </div>
                </FormLayout.Group>
                <FormLayout.Group>
                    <Input label={t("Reward Name")} onChange={handleInputChange} value={state?.name} id="name" placeholder={t("Reward Name")} />
                </FormLayout.Group>

                <FormLayout.Group>
                     <Select
                        onChange={val => setState(prev => ({ ...prev, application: val }))}
                        placeholder={t("Select Application")}
                        allowClear
                        showDropDownOnFocus={true}
                        usePortal
                        defaultValue={state?.application}
                        label={t("Apply To")}
                    >
                        {[
                            // {id: "all_products", label: `${t("All Products")}`}, 
                            {id: "product_categories", label: `${t("Specific Product Category")}`}, 
                            {id: "product", label: `${t("Specific Product")}`}, 
                            {id: "order", label: `${t("Order Subtotal")}`}]?.filter(option => state?.reward_type === "free_product" ? option?.id !== "order" : true)?.map((option, index) => {
                        return (
                            <Option key={index} value={option?.id} extraValues={option}>
                            {option?.label}
                            </Option>
                        );
                        })}
                    </Select>
                    
                    
                </FormLayout.Group>

                {state?.application === "product_categories" ? (<>
                    <FormLayout.Group>
                         <Select
                            showSearch
                            onSearch={(query: string) => setTypeSearch(query)}
                            onChange={(_, extraValue: TProductType & { name?: string;}) => setSelectedTypes(prev => ([...prev, extraValue]))}
                            allowClear
                            searchValue={typeSearch}
                            searching={!types}
                            showDropDownOnFocus
                            usePortal
                            placeholder={t("Search product categories")}
                            noData={
                                <div className="flex items-center p-2 text-black">
                                <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                                <span className="ml-3 italic">{t("No product category found...")}</span>
                                </div>
                            }
                            >
                            {types?.filter(type => !selectedTypes?.some(selectedType => selectedType?.id === type?.id) && type?.title?.toLocaleLowerCase()?.includes(typeSearch?.toLocaleLowerCase()))?.map((type, index) => {
                                return (
                                <Option key={index} value={type?.id} extraValues={type}>
                                   <p>{type?.title}</p>
                                </Option>
                                );
                            })}
                            </Select>
                    </FormLayout.Group>
                    <FormLayout.Group>
                        {/* Selected product category tags */}
                        <div className="loyalty-tags-container">
                            {selectedTypes?.map((type, index) => {
                                return (
                                    <div key={index} className="loyalty-tag">
                                        <p className="loyalty-tag-text">{type?.title}</p>
                                        <FontAwesomeIcon icon={faXmark} className="loyalty-tag-x" onClick={() => setSelectedTypes(prev => prev?.filter(prevType => prevType?.id !== type?.id))} />
                                    </div>
                                );
                            })}
                        </div>
                        </FormLayout.Group>
                    </>
                    ) : state?.application === "product" ? <><FormLayout.Group>
                        <Select
                            showSearch
                            onSearch={(query: string) => setProductSearch(query)}
                            onChange={(_, extraValue: IProduct & {
                                name?: string;
                            }) => setSelectedProducts(prev => ([...prev, extraValue]))}
                            allowClear
                            searchValue={productSearch}
                            searching={!products}
                            showDropDownOnFocus
                            usePortal
                            placeholder={t("Search products")}
                            noData={
                                <div className="flex items-center p-2 text-black">
                                <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                                <span className="ml-3 italic">{t("No product found...")}</span>
                                </div>
                            }
                            >
                            {products?.filter(product => !selectedProducts?.some(selectedProduct => selectedProduct?.id === product?.id))?.map((product, index) => {
                                return (
                                <Option key={index} value={product?.id} extraValues={product}>
                                   <p>{product?.preferred_title ? product?.preferred_title : product?.title}</p>
                                   {product?.subtitle ? <p className="loyalty-product-subtitle">{product?.subtitle}</p> : null}
                                </Option>
                                );
                            })}
                            </Select>
                        </FormLayout.Group>
                        <FormLayout.Group>
                        {/* Selected product tags */}
                        <div className="loyalty-tags-container">
                            {selectedProducts?.map((product, index) => {
                                return (
                                    <div key={index} className="loyalty-tag">
                                        <p className="loyalty-tag-text">{product?.preferred_title ? product?.preferred_title : product?.title} {product?.subtitle ? ` • ${product?.subtitle}` : null}</p>
                                        <FontAwesomeIcon icon={faXmark} className="loyalty-tag-x" onClick={() => setSelectedProducts(prev => prev?.filter(prevProduct => prevProduct?.id !== product?.id))} />
                                    </div>
                                );
                            })}
                        </div>
                        </FormLayout.Group>
                         </> : null}

                <FormLayout.Group>
                    <Input 
                    prefix={state?.reward_type === "discount_percent" || state?.reward_type === "free_product" ? "%" : getCurrencySymbol(facilityStore?.facility?.default_currency_code)}
                    type="number" 
                    label={state?.reward_type === "discount_percent" || state?.reward_type === "free_product" ? t("Discount (%)") : `${t("Discount")} (${getCurrencySymbol(facilityStore?.facility?.default_currency_code)})`} 
                    onChange={handleInputChange} 
                    value={state?.discount_value} 
                    id="discount_value" 
                    disabled={state?.reward_type === "free_product"}
                    min="0"
                    placeholder={"10"} />
                    <Input 
                    suffix={t("points")}
                    type="number" 
                    label={t("Points Amount")} 
                    onChange={handleInputChange} 
                    value={state?.points_required} 
                    id="points_required" 
                    min="0"
                    step="1"
                    placeholder={"100"} />
                </FormLayout.Group>
                </FormLayout>
            </Sheet>

        </Portal>

    );

}